<template>
  <!-- Mobile: bottom sheet drawer (커스텀 헤더로 기본 헤더 잔여요소 제거) -->
  <el-drawer
    v-if="isMobile"
    v-model="innerVisible"
    direction="btt"
    :size="drawerSize"
    :with-header="false"
    :before-close="beforeClose"
    :close-on-click-modal="closeOnClickModal"
    append-to-body
    :destroy-on-close="destroyOnClose"
    class="rd-drawer"
    @close="handleClose"
    @opened="$emit('opened')"
    @closed="$emit('closed')"
  >
    <div class="rd-sheet">
      <div class="rd-grabber" />
      <div class="rd-sheet-header">
        <slot name="header">
          <span class="rd-heading">
            <span class="rd-title">{{ title }}</span>
            <span v-if="subtitle" class="rd-sub">{{ subtitle }}</span>
          </span>
        </slot>
        <button type="button" class="rd-close" aria-label="닫기" @click="closeSheet">
          <el-icon><Close /></el-icon>
        </button>
      </div>
      <div class="rd-body rd-body--mobile">
        <slot />
      </div>
      <div v-if="$slots.footer" class="rd-footer" :class="{ 'rd-footer--spread': footerSpread }">
        <slot name="footer" />
      </div>
    </div>
  </el-drawer>

  <!-- Desktop: centered dialog -->
  <el-dialog
    v-else
    v-model="innerVisible"
    :title="title"
    :width="resolvedWidth"
    :before-close="beforeClose"
    :close-on-click-modal="closeOnClickModal"
    append-to-body
    :destroy-on-close="destroyOnClose"
    top="8vh"
    class="rd-dialog"
    @close="handleClose"
    @opened="$emit('opened')"
    @closed="$emit('closed')"
  >
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>
    <template v-else-if="subtitle" #header>
      <span class="rd-heading">
        <span class="el-dialog__title">{{ title }}</span>
        <span class="rd-sub">{{ subtitle }}</span>
      </span>
    </template>
    <div class="rd-body rd-body--desktop">
      <slot />
    </div>
    <template v-if="$slots.footer" #footer>
      <div class="rd-footer-desktop" :class="{ 'rd-footer--spread': footerSpread }">
        <slot name="footer" />
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { computed } from 'vue'
import { Close } from '@element-plus/icons-vue'
import { useBreakpoint } from '@/composables/useBreakpoint'

export default {
  name: 'ResponsiveDialog',
  components: { Close },
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    // 제목 아래 한 줄 — 영업 다이얼로그에서 '어느 매장 일인지'. 모든 영업 창이 제목 → 매장 → 본문 → 버튼 순서다.
    subtitle: { type: String, default: '' },
    width: { type: [String, Number], default: '560px' },
    footerSpread: { type: Boolean, default: false },
    mobileSize: { type: String, default: 'auto' },
    beforeClose: { type: Function, default: undefined },
    closeOnClickModal: { type: Boolean, default: true },
    destroyOnClose: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'opened', 'closed', 'close'],
  setup(props, { emit }) {
    const { isMobile } = useBreakpoint()

    const innerVisible = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v)
    })

    const resolvedWidth = computed(() => {
      const raw = props.width
      if (raw == null) return 'min(560px, 92vw)'
      if (typeof raw === 'number') return `min(${raw}px, 92vw)`
      const s = String(raw).trim()
      if (s === '100%' || s.endsWith('vw')) return s
      return `min(${s}, 92vw)`
    })

    // 'auto'면 내용 높이에 맞춰지되 최대 90vh로 제한 (CSS에서 처리)
    const drawerSize = computed(() => (props.mobileSize === 'auto' ? 'auto' : props.mobileSize))

    function handleClose() {
      emit('close')
    }
    function closeSheet() {
      innerVisible.value = false
    }

    return { isMobile, innerVisible, resolvedWidth, drawerSize, handleClose, closeSheet }
  }
}
</script>

<style scoped>
.rd-body {
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

/* ---------- Mobile bottom sheet ---------- */
.rd-drawer :deep(.el-drawer__body) {
  padding: 0;
  overflow: hidden;
}

.rd-drawer {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  height: auto !important;
  /*
   * dvh 를 쓴다. iOS Safari 에서 100vh 는 주소창을 **포함한** 높이라
   * 92vh 시트는 실제 보이는 영역보다 커질 수 있고, 그러면 푸터의
   * 저장/요청 버튼이 화면 밖으로 밀려 누를 수가 없다.
   */
  max-height: 92dvh;
}

.rd-sheet {
  display: flex;
  flex-direction: column;
  max-height: 92dvh;
}

@supports not (height: 100dvh) {
  .rd-drawer { max-height: 88vh; }
  .rd-sheet { max-height: 88vh; }
}

.rd-grabber {
  width: 40px;
  height: 4px;
  border-radius: var(--r-full);
  background: var(--color-border-strong, #d0d5dd);
  margin: 8px auto 4px;
  flex-shrink: 0;
}

.rd-sheet-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 12px;
  border-bottom: 1px solid var(--color-border, #e4e7ec);
  flex-shrink: 0;
}

.rd-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.rd-title {
  font-size: var(--fs-lead);
  font-weight: var(--fw-bold);
  color: var(--color-text, #1f2328);
}

.rd-sub {
  font-size: var(--fs-sm);
  font-weight: var(--fw-regular);
  color: var(--color-text-subtle);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 시각 원은 36px 로 두고 히트 영역만 --tap-min(44px)으로 넓힌다(::after).
   원 자체를 44px 로 키우면 헤더가 8px 높아져 모든 시트의 상단이 두꺼워진다.
   이 버튼은 헤더 오른쪽 끝이라 확장분이 제목 영역을 덮지 않는다. */
.rd-close {
  position: relative;
  margin-left: auto;
  width: 36px;
  height: 36px;
  border: none;
  background: var(--color-surface-2, #f1f3f6);
  border-radius: var(--r-full);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted, #6b7280);
  cursor: pointer;
  flex-shrink: 0;
}

.rd-close::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--tap-min, 44px);
  height: var(--tap-min, 44px);
  transform: translate(-50%, -50%);
}

.rd-close:active {
  background: var(--color-border, #e4e7ec);
}

.rd-body--mobile {
  padding: 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1 1 auto;
}

.rd-footer {
  display: flex;
  gap: 10px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--color-border, #e4e7ec);
  flex-shrink: 0;
}

/* 모든 푸터 버튼은 모바일에서 충분한 탭 영역 확보 */
.rd-footer :deep(.el-button) {
  min-height: 44px;
}

/* 푸터 슬롯에 버튼을 그대로 둔 단순 다이얼로그는 풀폭 균등 배치 */
.rd-footer > :deep(.el-button) {
  flex: 1;
  margin-left: 0;
}

.rd-footer--spread {
  justify-content: space-between;
}

/* ---------- Desktop ---------- */

/*
 * 데스크톱 본문도 **자기 자신이** 스크롤한다.
 *
 * 이 규칙이 없으면 el-dialog 는 본문에 max-height 를 두지 않아서, 긴 다이얼로그는
 * .el-overlay-dialog(페이지 오버레이) 가 대신 스크롤한다. 두 가지가 어긋난다:
 *   1) 제목과 푸터 버튼이 같이 밀려 올라가 '저장' 이 화면 밖으로 나간다.
 *      모바일 시트(.rd-body--mobile)는 이미 본문만 스크롤한다 — 같은 다이얼로그가
 *      화면 폭에 따라 다르게 움직이고 있었다.
 *   2) 본문 안의 position:sticky (알림함의 날짜 라벨 등) 가 붙을 스크롤 조상을
 *      찾지 못해 조용히 죽는다. sticky 는 가장 가까운 '스크롤하는' 조상만 본다.
 *
 * 62vh: EP 다이얼로그는 top:15vh 에서 시작하고 헤더 ~54 + 푸터 ~60 을 쓴다.
 * 62vh 면 900px 높이 화면에서 아래로 넘치지 않는다.
 */
.rd-body--desktop {
  max-height: 62vh;
  overflow-y: auto;
}

.rd-footer-desktop {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2, 8px);
  width: 100%;
}

.rd-footer-desktop.rd-footer--spread {
  justify-content: space-between;
}
</style>

<style>
/*
 * 데스크톱 창 틀 — el-dialog 는 body 로 옮겨져 그려져서 scoped 가 닿지 않는다. 클래스(rd-dialog)로만 고른다.
 * 제목 줄 · 본문 · 버튼 줄을 선으로 나누고, 버튼 줄은 한 톤 낮은 바탕에 둔다 — 어디서 끝나고 무엇을 누르는지가 먼저 보인다.
 */
.rd-dialog.el-dialog {
  --el-dialog-padding-primary: 0;
  --el-dialog-border-radius: var(--r-xl);
  --el-dialog-box-shadow: var(--sh-3);
  overflow: hidden;
}

.rd-dialog .el-dialog__header {
  padding: var(--sp-5) 56px var(--sp-4) var(--sp-6);
  cursor: default;
}

.rd-dialog .el-dialog__title {
  font-size: var(--fs-lead);
  font-weight: var(--fw-bold);
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.rd-dialog .el-dialog__headerbtn {
  top: var(--sp-3);
  right: var(--sp-3);
  width: 40px;
  height: 40px;
  border-radius: var(--r-md);
}

.rd-dialog .el-dialog__headerbtn:hover {
  background: var(--color-surface-2);
}

.rd-dialog .el-dialog__body {
  padding: var(--sp-1) var(--sp-6) var(--sp-5);
}

.rd-dialog .el-dialog__footer {
  padding: var(--sp-3) var(--sp-6);
  border-top: 1px solid var(--color-border);
  background: var(--color-surface-3);
}
</style>
